import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { calculateSewoon, type SewoonResult } from '@/utils/sewoonCalculations';
import type { SajuResult } from '@/utils/sajuCalculations';

interface SewoonSectionProps {
  saju: SajuResult;
}

// 토스 스타일 게이지
function TossGauge({ 
  value, 
  label, 
  trend,
  color 
}: { 
  value: number; 
  label: string; 
  trend: 'up' | 'stable' | 'down';
  color: string;
}) {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setWidth(value), 300);
    return () => clearTimeout(timer);
  }, [value]);

  const trendIcon = trend === 'up' ? '↗' : trend === 'down' ? '↘' : '→';
  const trendColor = trend === 'up' ? 'text-emerald-500' : trend === 'down' ? 'text-rose-500' : 'text-gray-400';

  return (
    <div className="bg-white rounded-2xl p-4 border border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <div className="flex items-center gap-1">
          <span className={`text-xs ${trendColor}`}>{trendIcon}</span>
          <span className="text-lg font-bold text-gray-900">{value}</span>
        </div>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${width}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

const MONTH_ABBR_EN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// 월별 달력 셀
function MonthCell({ month, isSelected, onClick }: { month: SewoonResult['monthly'][0]; isSelected: boolean; onClick: () => void }) {
  const { t, i18n } = useTranslation();
  const monthLabel = i18n.language === 'en'
    ? MONTH_ABBR_EN[month.month - 1]
    : `${month.month}${t('monthly.month.suffix')}`;
  const scoreColor = month.score >= 75 ? 'bg-emerald-100 text-emerald-700' : 
                     month.score >= 60 ? 'bg-blue-100 text-blue-700' : 
                     month.score >= 45 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700';

  return (
    <button
      onClick={onClick}
      className={`p-3 rounded-xl text-center transition-all ${
        isSelected ? 'bg-gray-900 text-white ring-2 ring-gray-900 ring-offset-2' : 'bg-gray-50 hover:bg-gray-100'
      }`}
    >
      <span className={`text-xs font-medium ${isSelected ? 'text-white/70' : 'text-gray-500'}`}>{monthLabel}</span>
      <div className={`text-xs mt-1 px-1.5 py-0.5 rounded-full inline-block ${isSelected ? 'bg-white/20' : scoreColor}`}>
        {month.ganji}
      </div>
    </button>
  );
}

export default function SewoonSection({ saju }: SewoonSectionProps) {
  const { t, i18n } = useTranslation();
  const getMonthLabel = (m: number) =>
    i18n.language === 'en' ? MONTH_ABBR_EN[m - 1] : `${m}${t('monthly.month.suffix')}`;
  const [sewoon, setSewoon] = useState<SewoonResult | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(4); // 4월 기본 선택
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    const lang = (i18n.language === 'en' ? 'en' : 'ko') as 'ko' | 'en';
    const result = calculateSewoon(saju, lang);
    setSewoon(result);
  }, [saju, i18n.language]);

  if (!sewoon) return null;

  const levelConfig = {
    excellent: { color: '#10B981', bg: 'bg-emerald-50', text: 'text-emerald-700', emoji: '✨', label: t('sewoon.level.excellent') },
    good: { color: '#3B82F6', bg: 'bg-blue-50', text: 'text-blue-700', emoji: '🌟', label: t('sewoon.level.good') },
    normal: { color: '#6B7280', bg: 'bg-gray-50', text: 'text-gray-700', emoji: '🌤', label: t('sewoon.level.normal') },
    challenging: { color: '#F59E0B', bg: 'bg-amber-50', text: 'text-amber-700', emoji: '⛅', label: t('sewoon.level.challenging') },
    difficult: { color: '#EF4444', bg: 'bg-rose-50', text: 'text-rose-700', emoji: '🌧', label: t('sewoon.level.difficult') },
  };

  const config = levelConfig[sewoon.overallLevel];
  const selectedMonthData = sewoon.monthly[selectedMonth - 1];

  return (
    <div className="bg-white rounded-3xl p-5 border border-gray-100">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-bold text-gray-900">{sewoon.year}{t('sewoon.title.suffix')}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{t('sewoon.subtitle')}</p>
        </div>
        <div className={`px-3 py-1.5 rounded-full ${config.bg} ${config.text} text-sm font-bold flex items-center gap-1`}>
          <span>{config.emoji}</span>
          <span>{config.label}</span>
        </div>
      </div>

      {/* 총점 카드 */}
      <div className={`${config.bg} rounded-2xl p-5 mb-5`}>
        <div className="flex items-center justify-between mb-3">
          <span className={`text-sm font-medium ${config.text}`}>{t('sewoon.score')}</span>
          <span className="text-3xl font-black text-gray-900">{sewoon.overallScore}</span>
        </div>
        <div className="h-3 bg-white/50 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-1000"
            style={{ width: `${sewoon.overallScore}%`, backgroundColor: config.color }}
          />
        </div>
        <p className={`text-sm mt-3 leading-relaxed ${config.text}`}>{sewoon.summary}</p>
      </div>

      {/* 카테고리별 게이지 */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <TossGauge
          value={sewoon.categories.career.score}
          label={t('sewoon.career')}
          trend={sewoon.categories.career.trend}
          color="#3B82F6"
        />
        <TossGauge
          value={sewoon.categories.love.score}
          label={t('sewoon.love')}
          trend={sewoon.categories.love.trend}
          color="#EC4899"
        />
        <TossGauge
          value={sewoon.categories.money.score}
          label={t('sewoon.money')}
          trend={sewoon.categories.money.trend}
          color="#10B981"
        />
        <TossGauge
          value={sewoon.categories.health.score}
          label={t('sewoon.health')}
          trend={sewoon.categories.health.trend}
          color="#F59E0B"
        />
      </div>

      {/* 카테고리 메시지 */}
      <div className="space-y-2 mb-5">
        <div className="flex items-start gap-2 text-sm">
          <span className="text-blue-500 mt-0.5">💼</span>
          <span className="text-gray-600">{sewoon.categories.career.message}</span>
        </div>
        <div className="flex items-start gap-2 text-sm">
          <span className="text-pink-500 mt-0.5">💕</span>
          <span className="text-gray-600">{sewoon.categories.love.message}</span>
        </div>
        <div className="flex items-start gap-2 text-sm">
          <span className="text-emerald-500 mt-0.5">💰</span>
          <span className="text-gray-600">{sewoon.categories.money.message}</span>
        </div>
        <div className="flex items-start gap-2 text-sm">
          <span className="text-amber-500 mt-0.5">💪</span>
          <span className="text-gray-600">{sewoon.categories.health.message}</span>
        </div>
      </div>

      {/* 월별 운세 */}
      <div className="border-t border-gray-100 pt-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-gray-900">{t('sewoon.monthly.title')}</h4>
          <button
            onClick={() => setShowDetail(!showDetail)}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 cursor-pointer"
          >
            {showDetail ? t('sewoon.monthly.collapse') : t('sewoon.monthly.detail')}
            <i className={showDetail ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
          </button>
        </div>

        {showDetail && selectedMonthData && (
          <div className="bg-gray-50 rounded-xl p-4 mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-gray-900">{getMonthLabel(selectedMonthData.month)}</span>
              <span className={`text-xs px-2 py-1 rounded-full ${
                selectedMonthData.score >= 75 ? 'bg-emerald-100 text-emerald-700' :
                selectedMonthData.score >= 60 ? 'bg-blue-100 text-blue-700' :
                selectedMonthData.score >= 45 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'
              }`}>
                {selectedMonthData.highlight}
              </span>
            </div>
            <p className="text-sm text-gray-600">{selectedMonthData.message}</p>
          </div>
        )}

        <div className="grid grid-cols-6 gap-2">
          {sewoon.monthly.map((month) => (
            <MonthCell
              key={month.month}
              month={month}
              isSelected={selectedMonth === month.month}
              onClick={() => {
                setSelectedMonth(month.month);
                setShowDetail(true);
              }}
            />
          ))}
        </div>
      </div>

      {/* 행운 정보 */}
      <div className="border-t border-gray-100 pt-5 mt-5">
        <h4 className="text-sm font-bold text-gray-900 mb-3">{sewoon.year}{t('sewoon.lucky.title')}</h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-3 text-center">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center mx-auto mb-2 shadow-sm">
              <i className="ri-palette-line text-purple-500" />
            </div>
            <p className="text-xs text-gray-500 mb-1">{t('sewoon.lucky.color')}</p>
            <p className="text-sm font-bold text-gray-900">{sewoon.lucky.colors[0]}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-3 text-center">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center mx-auto mb-2 shadow-sm">
              <i className="ri-number-1 text-blue-500" />
            </div>
            <p className="text-xs text-gray-500 mb-1">{t('sewoon.lucky.number')}</p>
            <p className="text-sm font-bold text-gray-900">{sewoon.lucky.numbers[0]}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-3 text-center">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center mx-auto mb-2 shadow-sm">
              <i className="ri-compass-3-line text-amber-500" />
            </div>
            <p className="text-xs text-gray-500 mb-1">{t('sewoon.lucky.direction')}</p>
            <p className="text-sm font-bold text-gray-900">{sewoon.lucky.directions[0]}</p>
          </div>
        </div>
      </div>

      {/* 조언 */}
      <div className="border-t border-gray-100 pt-5 mt-5">
        <h4 className="text-sm font-bold text-gray-900 mb-3">{t('sewoon.advice.title')}</h4>
        <div className="space-y-2">
          {sewoon.advice.map((tip, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="w-5 h-5 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                {i + 1}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{tip}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
